import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAdminPlayers, updatePlayerStatus, Player } from '../api/admin.api';
import LoadingSpinner from '../components/LoadingSpinner';

const AdminPendingRegistrationsPage: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string[]>(['PAYMENT_SUBMITTED', 'PENDING_APPROVAL']);

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAdminPlayers({ status: statusFilter });
      setPlayers(response.data || []);
    } catch (err) {
      // Silent fail for prototype
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const handleStatusChange = async (playerId: string, newStatus: 'APPROVED' | 'DECLINED' | 'PENDING_APPROVAL') => {
    try {
      await updatePlayerStatus(playerId, { status: newStatus });
      // Update local state
      setPlayers(prev => prev.map(p => 
        p.id === playerId ? { ...p, status: newStatus } : p
      ));
    } catch (err) {
      // Silent fail for prototype
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-success';
      case 'DECLINED':
        return 'bg-danger';
      case 'PENDING_APPROVAL':
        return 'bg-warning text-white';
      case 'PAYMENT_SUBMITTED':
        return 'bg-info text-white';
      default:
        return 'bg-secondary';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">Pending Registrations</h1>
          <p className="text-muted-soft mb-0">Review and manage player registrations</p>
        </div>
      </div>

      <div className="card card-surface mb-3">
        <div className="card-body">
          <div className="d-flex flex-column flex-md-row flex-wrap gap-2 align-items-start align-items-md-center">
            <span className="fw-semibold mb-2 mb-md-0">Filter by Status:</span>
            <div className="d-flex flex-wrap gap-2">
            {['PAYMENT_SUBMITTED', 'PENDING_APPROVAL', 'APPROVED', 'DECLINED'].map(status => (
              <button
                key={status}
                type="button"
                className={`btn btn-sm ${statusFilter.includes(status) ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => {
                  if (statusFilter.includes(status)) {
                    setStatusFilter(prev => prev.filter(s => s !== status));
                  } else {
                    setStatusFilter(prev => [...prev, status]);
                  }
                }}
              >
                {status.replace('_', ' ')}
              </button>
            ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card card-surface">
        <div className="card-body">
          {players.length === 0 ? (
            <div className="alert alert-secondary mb-0">No players found with selected statuses.</div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="table-responsive d-none d-md-block">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Player Name</th>
                    <th>Phone Number</th>
                    <th>Payment Method</th>
                    <th>Transaction ID</th>
                    <th>Current Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((player) => (
                    <tr key={player.id}>
                      <td className="fw-semibold">{player.name}</td>
                      <td>{player.phone}</td>
                      <td>{player.payment_method || 'N/A'}</td>
                      <td>
                        <code className="small">{player.transaction_id || 'N/A'}</code>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(player.status)} badge-status`}>
                          {player.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => handleStatusChange(player.id, 'APPROVED')}
                            disabled={player.status === 'APPROVED'}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleStatusChange(player.id, 'DECLINED')}
                            disabled={player.status === 'DECLINED'}
                          >
                            Decline
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={() => handleStatusChange(player.id, 'PENDING_APPROVAL')}
                            disabled={player.status === 'PENDING_APPROVAL'}
                          >
                            Pending
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {players.map((player) => (
                  <div key={player.id} className="card card-surface mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-bold">{player.name}</h6>
                          <div className="small text-muted-soft mb-1">
                            <i className="bi bi-telephone me-1"></i>
                            {player.phone}
                          </div>
                          <div className="small text-muted-soft mb-1">
                            <i className="bi bi-credit-card me-1"></i>
                            {player.payment_method || 'N/A'}
                          </div>
                          <div className="small text-muted-soft mb-1">
                            <i className="bi bi-receipt me-1"></i>
                            <code>{player.transaction_id || 'N/A'}</code>
                          </div>
                        </div>
                        <span className={`badge ${getStatusBadgeClass(player.status)} badge-status`}>
                          {player.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="d-grid gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={() => handleStatusChange(player.id, 'APPROVED')}
                          disabled={player.status === 'APPROVED'}
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Approve
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleStatusChange(player.id, 'DECLINED')}
                          disabled={player.status === 'DECLINED'}
                        >
                          <i className="bi bi-x-circle me-1"></i>
                          Decline
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          onClick={() => handleStatusChange(player.id, 'PENDING_APPROVAL')}
                          disabled={player.status === 'PENDING_APPROVAL'}
                        >
                          <i className="bi bi-clock me-1"></i>
                          Set Pending
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPendingRegistrationsPage;


